import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import appConfig from 'app-config';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { MLNameDetailsComponent } from 'ml-components-common-react';
import { MLVehicleOperatorStatusComponent } from 'ml-capability-policychange-common-react';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';

import messages from './MLAddVehicleOperatorComponent.messages';
import metadata from './MLAddVehicleOperatorComponent.metadata.json5';
import styles from './MLAddVehicleOperatorComponent.module.scss';

const MLAddVehicleOperatorComponent = (props) => {
    const {
        vehicleOperator,
        driverType,
        canExcludeDrivers,
        onValidate,
        showValidationErrors,
        vehicleOperatorsVM,
        checkUniqueSelf,
        checkUniqueSpouse,
        submissionVM
    } = props;

    const {
        getFilteredGender
    } = MLDriverVehicleUtil();

    const isAMP = appConfig.portalCode === 'AMP';
    const { refreshData } = useDataRefresh();
    const translator = useTranslator();
    const inputWidth = isAMP ? classNames('remove-form-group-spacing', 'col-md-6') : classNames('remove-form-group-spacing', 'col-md-4');
    const isPersonalUmbrella = submissionVM.value.isPersonalUmbrella();
    const isModernProduct = _.get(submissionVM, 'isModernProduct_Ext.value', false);
    const isUMB_NP = isPersonalUmbrella && isModernProduct;
    const validateDriver = () => {
        vehicleOperator.contact.isGenderRequired.value = true;
        vehicleOperator.isRelationshipRequired.value = true;
        vehicleOperator.isDriverStatusForAddingRequired.value = true;
        vehicleOperator.isDriverTypeRequired.value = true;

        switch (driverType) {
            case 'rated':
                vehicleOperator.driverStatusForAdding.value = 'insure_driver';
                vehicleOperator.contact.isMaritalStatusRequired.value = true;
                vehicleOperator.contact.isDateOfBirthRequired.value = true;
                break;
            case 'excluded':
                vehicleOperator.contact.isDateOfBirthRequired.value = true;
                break;
            default:
                vehicleOperator.contact.isMaritalStatusRequired.value = false;
                vehicleOperator.contact.isDateOfBirthForNonDriverRequired.value = true;
                break;

        }
    };

    useEffect(() => {
        validateDriver();
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangeDOB = () => {
        if (driverType) {
            switch (driverType) {
                case 'household':
                    if (!_.isUndefined(vehicleOperator.contact.dateOfBirthForNonDriver)) {
                        vehicleOperator.contact.dateOfBirth = vehicleOperator.contact.dateOfBirthForNonDriver;
                    }
                    break;
                default:
                    if (!_.isUndefined(vehicleOperator.contact.dateOfBirth.value)) {
                        vehicleOperator.contact.dateOfBirthForNonDriver.value = vehicleOperator.contact.dateOfBirth.value;
                    }
                    break;
            }
        }
    };

    const generateTooltip = (title, content, ...isDouble) => {
        if (isDouble) {
            return isAMP ? '' : {
                title: (
                    <FormattedMessage
                        {...title}
                        values={{
                            break: <br />
                        }}
                    />),
                text: (
                    <FormattedMessage
                        {...content}
                        values={{
                            break: <><br /><br /></>
                        }}
                    />),
                placement: "bottom"
            }
        }
        return isAMP ? '' : {
            title: translator(title),
            text: translator(content),
            placement: "bottom"
        }
    }

    const updateDriverVM = () => {
        refreshData();
    };

    const nbGenderState = _.get(submissionVM, 'baseData.isValidNonBinaryGenderState_Ext.value');

    const overrideProps = {
        nameDetailsComponent: {
            layout: isAMP ? 'ML-Split' : 'ML-Name-Default',
            showLabels: isAMP,
            personVM: vehicleOperator.contact,
            onValidate,
            showValidationErrors,
            onModelChange: updateDriverVM
        },
        relationshipControl: {
            className: inputWidth
        },
        dobRow: {
            visible: driverType !== 'rated'
        },
        ratedDobTooltipContainer: {
            visible: isAMP
        },
        dobControl: {
            className: inputWidth
        },
        dob: {
            onBlur: () => onChangeDOB(),
            path: driverType === 'household' ? 'contact.dateOfBirthForNonDriver' : 'contact.dateOfBirth'
        },
        dobTooltipContainer: {
            className: isAMP ? 'col-md-6' : 'col-md-8'
        },
        genderRow: {
            visible: driverType !== 'rated',
            availableValues: getFilteredGender(nbGenderState)
        },
        ratedGenderRow: {
            availableValues: getFilteredGender(nbGenderState)
        },
        ratedOnlyInputs: {
            visible: driverType === 'rated'
        },
        maritalStatusControl: {
            className: inputWidth
        },
        maritalStatus: {
            tooltip: generateTooltip(messages.maritalStatusTipTitle, messages.maritalStatusTip, true)
        },
        maritalStatusTooltipContainer: {
            visible: isAMP
        },
        ageControl: {
            className: inputWidth
        },
        checkUniqueSelf: {
            visible: vehicleOperatorsVM && !checkUniqueSelf(vehicleOperatorsVM.value)
        },
        checkUniqueSpouse: {
            visible: vehicleOperatorsVM && !checkUniqueSpouse(vehicleOperatorsVM.value)
        },
        stateControl: {
            className: inputWidth
        },
        state: {
            tooltip: generateTooltip(messages.stateTipTitle, messages.stateTip)
        },
        stateTooltipContainer: {
            visible: isAMP
        },
        licenseControl: {
            className: inputWidth
        },
        licenseNumber: {
            tooltip: generateTooltip(messages.dontHaveThis, messages.informationRequiredLater)
        },
        licenseTooltipContainer: {
            visible: isAMP
        },
        reasonRow: {
            visible: driverType !== 'rated'
        },
        pupVehicleOperatorStatusComponent: {
            vehicleOperator,
            label: translator(messages.reasonNotListedAsDriver),
            driverType: vehicleOperator.driverType.value.code,
            canExcludeDrivers,
            isPolicyChange: true,
            onValidate,
            showValidationErrors,
            isModernProduct,
            isUMB_NP
        }
    };

    const resolvers = {
        resolveComponentMap: {
            mlnamedetailscomponent: MLNameDetailsComponent,
            mlVehicleOperatorStatusComponent: MLVehicleOperatorStatusComponent
        },
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={vehicleOperator}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            onModelChange={updateDriverVM}
            onValidationChange={onValidate}
            showErrors={showValidationErrors}
        />
    );
};

MLAddVehicleOperatorComponent.propTypes = {
    vehicleOperator: PropTypes.object,
    driverType: PropTypes.string,
    canExcludeDrivers: PropTypes.bool,
    onValidate: PropTypes.func,
    showValidationErrors: PropTypes.bool
}

MLAddVehicleOperatorComponent.defaultProps = {
    showValidationErrors: false
}

export default MLAddVehicleOperatorComponent;
